import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import { MdClose } from "react-icons/md";
import { IconButton } from "components/button";

// Chakra runs headless (no ChakraProvider/theme): the full-viewport container
// and stacking are set explicitly. Sits below Dialog (z 70) so dialogs opened
// from a drawer stack on top.
const DRAWER_Z_INDEX = 60;

const SIZES = {
  sm: "sm:max-w-[min(360px,calc(100vw_-_1rem))]",
  md: "sm:max-w-[min(480px,calc(100vw_-_1rem))]",
  lg: "sm:max-w-[min(640px,calc(100vw_-_1rem))]",
};

const CONTAINER_PROPS = {
  zIndex: DRAWER_Z_INDEX,
  top: 0,
  bottom: 0,
  height: "100%",
};

/**
 * SideDrawer — right-side panel for record details and forms.
 * Full width on mobile; fixed width from `sm` up.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose Called on Esc, overlay click and the close button.
 * @param {React.ReactNode} [props.title] Heading (labels the drawer).
 * @param {React.ReactNode} [props.subtitle] Muted text under the title.
 * @param {React.ReactNode} [props.headerActions] Compact actions (e.g. a small ghost Button) at the
 *   right of the header, beside the close button.
 * @param {React.ReactNode} [props.footer] Sticky footer, right-aligned actions with gap-2.
 * @param {"sm"|"md"|"lg"} [props.size="md"] 360 / 480 / 640 px.
 * @param {React.RefObject<HTMLElement>} [props.initialFocusRef] Element focused on open (defaults to the first focusable).
 * @param {React.ReactNode} props.children Scrollable body content.
 */
function SideDrawer({
  isOpen,
  onClose,
  title,
  subtitle,
  headerActions,
  footer,
  size = "md",
  initialFocusRef,
  children,
}) {
  const hasHeader = Boolean(title || subtitle || headerActions);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="right"
      trapFocus
      returnFocusOnClose
      closeOnEsc
      closeOnOverlayClick
      initialFocusRef={initialFocusRef}
    >
      <ModalOverlay
        className="bg-overlay/60 backdrop-blur-[2px]"
        zIndex={DRAWER_Z_INDEX}
      />
      <DrawerContent
        containerProps={CONTAINER_PROPS}
        className={`flex flex-col overflow-hidden bg-surface text-fg shadow-overlay sm:my-2 sm:mr-2 sm:rounded-card ${
          SIZES[size] || SIZES.md
        }`}
      >
        {hasHeader && (
          <div className="flex shrink-0 items-start gap-3 border-b border-line px-6 py-5 pr-14">
            <div className="min-w-0 flex-1">
              {title && (
                <DrawerHeader as="h2" className="text-heading">
                  {title}
                </DrawerHeader>
              )}
              {subtitle && (
                <p className="mt-1 text-sm text-fg-muted">{subtitle}</p>
              )}
            </div>
            {headerActions && (
              <div className="-mt-1 flex shrink-0 items-center gap-2">{headerActions}</div>
            )}
          </div>
        )}
        <DrawerBody className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          {children}
        </DrawerBody>
        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line bg-surface px-6 py-4">
            {footer}
          </div>
        )}
        {/* Last in DOM order so initial focus lands on the first body/footer control. */}
        <IconButton
          icon={<MdClose />}
          label="Close"
          size="sm"
          onClick={onClose}
          className="absolute right-4 top-4"
        />
      </DrawerContent>
    </Drawer>
  );
}

export default SideDrawer;
